import { useEffect, useMemo, useRef, useState } from 'react';
import { subscribeToUsers } from '../../services/firebase';
import type { FirestoreUser } from '../../types';

interface UserMultiSelectProps {
  /** Currently-selected users (controlled). */
  selected: FirestoreUser[];
  onChange: (next: FirestoreUser[]) => void;
  /** Hard cap on how many users may be selected. */
  maxSelection?: number;
  disabled?: boolean;
}

/**
 * Typeahead multi-select user picker.
 *
 * The admin user set is small enough to load entirely client-side (this mirrors
 * UserManagement, which subscribes to the whole `users` collection), so we
 * filter in-memory by email/displayName rather than firing per-keystroke
 * Firestore queries. The input is debounced only to keep the filtered list from
 * recomputing on every keystroke.
 */
export function UserMultiSelect({
  selected,
  onChange,
  maxSelection = 100,
  disabled = false,
}: UserMultiSelectProps) {
  const [allUsers, setAllUsers] = useState<FirestoreUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = subscribeToUsers((users) => {
      setAllUsers(users);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Debounce the search term so filtering doesn't run on every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(term), 200);
    return () => clearTimeout(handle);
  }, [term]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedIds = useMemo(
    () => new Set(selected.map((u) => u.uid)),
    [selected],
  );

  const matches = useMemo(() => {
    const t = debouncedTerm.trim().toLowerCase();
    if (!t) return [] as FirestoreUser[];
    return allUsers
      .filter(
        (u) =>
          !selectedIds.has(u.uid) &&
          (u.email?.toLowerCase().includes(t) ||
            u.displayName?.toLowerCase().includes(t)),
      )
      .slice(0, 20);
  }, [allUsers, debouncedTerm, selectedIds]);

  const atCap = selected.length >= maxSelection;

  const addUser = (u: FirestoreUser) => {
    if (atCap || selectedIds.has(u.uid)) return;
    onChange([...selected, u]);
    setTerm('');
    setDebouncedTerm('');
  };

  const removeUser = (uid: string) => {
    onChange(selected.filter((u) => u.uid !== uid));
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selected.map((u) => (
            <span
              key={u.uid}
              className="inline-flex items-center gap-1.5 bg-brand-100 text-brand-700 text-xs rounded-full pl-3 pr-1.5 py-1"
            >
              <span className="font-medium">{u.email || u.uid}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeUser(u.uid)}
                  className="text-brand-500 hover:text-brand-800"
                  aria-label={`Remove ${u.email || u.uid}`}
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={term}
        disabled={disabled || atCap}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={
          atCap
            ? `Maximum ${maxSelection} users selected`
            : loading
              ? 'Loading users…'
              : 'Search users by email or name…'
        }
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50 disabled:text-gray-400"
      />

      {/* Dropdown */}
      {open && debouncedTerm.trim() && !atCap && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {matches.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {loading ? 'Loading users…' : 'No matching users'}
            </div>
          ) : (
            matches.map((u) => (
              <button
                key={u.uid}
                type="button"
                onClick={() => addUser(u)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex flex-col"
              >
                <span className="text-sm text-gray-900">
                  {u.email || 'No email'}
                </span>
                <span className="text-xs text-gray-500">
                  {u.displayName || 'No display name'}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
        <span>
          {selected.length} / {maxSelection} selected
        </span>
        {atCap && (
          <span className="text-amber-600">Selection cap reached</span>
        )}
      </div>
    </div>
  );
}
